import { BackendApiClient } from '../../auth/api/backend-client';
import { BackendValidationError } from '../../auth/api/investor';
import { requireList } from '../../../shared/lib/response-shape';
import type { ScopeContext } from '../../auth/model/types';

const backendClient = new BackendApiClient();

export type PositionSnapshotStatus = 'created' | 'processing' | 'success' | 'canceled' | 'failed';

export type PositionSnapshotOrder =
  | 'reference_time'
  | '-reference_time'
  | 'created'
  | '-created';

export const SETTLED_SNAPSHOT_STATUSES: PositionSnapshotStatus[] = ['success', 'failed'];

/**
 * The statuses a declaration never moves past — the settled set above, plus `canceled`,
 * which books nothing and is never waited on. Named for its composition rather than
 * "terminal" so it cannot be reached for by mistake where `SETTLED_SNAPSHOT_STATUSES`
 * (⊂ this set, excludes `canceled`) is the one actually meant.
 */
export const SETTLED_OR_CANCELED_SNAPSHOT_STATUSES: PositionSnapshotStatus[] = [
  ...SETTLED_SNAPSHOT_STATUSES,
  'canceled',
];

/**
 * Every status the backend can answer, as the keys of a `Record` over the whole union —
 * the same shape `parse-request.ts` uses for its own statuses, for the same
 * reason: a sixth status fails to compile here instead of silently landing outside both
 * lists below.
 */
const POSITION_SNAPSHOT_STATUSES = Object.keys({
  created: true,
  processing: true,
  success: true,
  canceled: true,
  failed: true,
} satisfies Record<PositionSnapshotStatus, true>) as PositionSnapshotStatus[];

/**
 * A declaration whose adjustment trade has not booked yet — the complement of
 * `SETTLED_OR_CANCELED_SNAPSHOT_STATUSES`, derived rather than typed out so the two can
 * never overlap or leave a gap. The booking marker counts these the same way it counts
 * `UNBOOKED_TRADE_STATUSES`.
 */
export const UNSETTLED_SNAPSHOT_STATUSES: PositionSnapshotStatus[] =
  POSITION_SNAPSHOT_STATUSES.filter(
    (status) => !SETTLED_OR_CANCELED_SNAPSHOT_STATUSES.includes(status),
  );

export interface PositionSnapshotQuery {
  portfolioId?: string;
  statuses?: PositionSnapshotStatus[];
  orderBy?: PositionSnapshotOrder;
}

export interface PositionSnapshot {
  id: string;
  status: PositionSnapshotStatus;
  portfolio_id: string;
  account_id: string;
  instrument_id: string;
  instrument_type: string;
  symbol?: string | null;
  symbol_amount: number;
  symbol_scale: number;
  price_amount?: number | null;
  price_scale?: number | null;
  currency?: string | null;
  reference_time: string;
  unique_id?: string | null;
  tags: string[];
  created: string;
}

export interface CreatePositionSnapshot {
  portfolio_id: string;
  instrument_id: string;
  symbol_amount: number;
  symbol_scale: number;
  price_amount?: number;
  price_scale?: number;
  reference_time: string;
  unique_id: string;
}

export interface PositionSnapshotResponse {
  snapshots: PositionSnapshot[];
  cursor?: string;
}

export async function createPositionSnapshots(
  sessionId: string,
  scopeContext: ScopeContext,
  snapshots: CreatePositionSnapshot[],
): Promise<PositionSnapshotResponse> {
  const response = await backendClient.post(
    '/position-snapshot', sessionId, { snapshots }, scopeContext,
  );
  if (response.success && response.data) {
    const body = response.data as PositionSnapshotResponse;
    return {
      ...body,
      snapshots: requireList<PositionSnapshot>(
        body, 'snapshots', 'Unreadable position snapshot response',
      ),
    };
  }
  if (response.validationError) {
    throw response.validationError;
  }
  throw new BackendValidationError(response.error || 'Failed to declare position snapshots');
}

/**
 * Lists snapshots, mirroring `getExternalTrades`: an absent status filter means no filter
 * at all, and a `cursor` on the response means the listing stopped short.
 */
export async function getPositionSnapshots(
  sessionId: string,
  scopeContext: ScopeContext,
  query: PositionSnapshotQuery = {},
): Promise<PositionSnapshotResponse> {
  const params = new URLSearchParams();
  if (query.portfolioId) params.set('portfolio_id', query.portfolioId);
  if (query.statuses?.length) params.set('status', query.statuses.join(','));
  if (query.orderBy) params.set('order_by', query.orderBy);
  const search = params.toString();
  const response = await backendClient.get(
    `/position-snapshot${search ? `?${search}` : ''}`,
    sessionId,
    scopeContext,
  );
  if (response.success && response.data) {
    const body = response.data as PositionSnapshotResponse;
    return {
      ...body,
      snapshots: requireList<PositionSnapshot>(
        body, 'snapshots', 'Unreadable position snapshot response',
      ),
    };
  }
  if (response.validationError) {
    throw response.validationError;
  }
  throw new BackendValidationError(response.error || 'Failed to fetch position snapshots');
}

export async function getPositionSnapshotById(
  sessionId: string,
  scopeContext: ScopeContext,
  snapshotId: string,
): Promise<PositionSnapshot | null> {
  const response = await backendClient.get(
    `/position-snapshot?ids=${snapshotId}`, sessionId, scopeContext,
  );
  if (response.success && response.data) {
    const snapshots = requireList<PositionSnapshot>(
      response.data as object, 'snapshots', 'Unreadable position snapshot response',
    );
    return snapshots[0] ?? null;
  }
  if (response.validationError) {
    throw response.validationError;
  }
  throw new BackendValidationError(response.error || 'Failed to fetch position snapshots');
}
